import React, { memo, useEffect, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';
import { FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import useSWR from 'swr';
import { parseISO, format } from 'date-fns';
import pt from 'date-fns/locale/pt';
import api from '../../services/api';
import { insertAtestado, updateAtestado } from '../../services/prontuario';
import {
  obterTextoTemplateAtestado,
  obterImagensPorTipoAtestado,
} from '../../helpers/documentos/atestados';
import {
  normalizeWhatsappPhone,
  enviarArquivo,
  getPdfBase64,
} from '../../services/pdfWhatsapp';
import {
  removeBeforeBase64,
  dataExtenso,
  openBase64Pdf,
} from '../../util/utils';
import { normalizarQuebrasLinha } from '../../util/textoMultilinha';
import generateAtestadoPDF from './generateAtestadoPDF';
import { signDocument, getVidaasStatus } from '../../services/vidaasV2';

const fetchAtestadosDoPaciente = async ([, codPaciente]) => {
  try {
    const result = await api.get(`paciente/${codPaciente}/atestados`);
    if (result.data.length > 0) {
      return result.data.map(atestado => ({
        ...atestado,
        dataFormatada: format(parseISO(atestado.DATAATESTADO), 'dd/MM/yyyy', {
          locale: pt,
        }),
      }));
    }
    return result.data;
  } catch (error) {
    toast.error(
      error?.response?.data?.error || 'Erro ao carregar atestados!'
    );
    throw error;
  }
};

const toAtestadoSelecionado = atestado => ({
  codAtestado: atestado?.CODATESTADO,
  codPaciente: atestado.CODPAC,
  codMedico: atestado?.CODMEDICO,
  nomeMedico: atestado?.NOMEMED,
  crm: atestado?.CRMMED,
  cpf: atestado?.CPFMED,
  rqe: atestado?.RQE,
  nomePaciente: atestado?.NOMEPAC,
  codConsulta: atestado?.CODCONSULTA,
  dataAtestado: atestado?.dataFormatada,
  tipo: atestado?.TIPOATESTADO,
  descricao: normalizarQuebrasLinha(atestado?.DESCRICAO),
});

const atestadoEmBranco = atualPaciente => ({
  codPaciente: atualPaciente?.CODPAC ?? '',
  codMedico: atualPaciente?.CODREALIZOU ?? '',
  nomeMedico: atualPaciente?.REALIZOU ?? '',
  nomePaciente: atualPaciente?.NOMEPAC ?? '',
  codConsulta: atualPaciente?.CODCONSULTA ?? '',
  dataAtestado: format(new Date(), 'dd/MM/yyyy'),
  tipo: '',
  descricao: '',
});

const montarDescricaoAtestado = (templateKey, nomePaciente, semData) => {
  const textoBase = obterTextoTemplateAtestado(templateKey);
  const descricao = textoBase.replace('@PAC', nomePaciente);
  if (semData) return descricao;
  return descricao.replace(
    '@MARILIA',
    `Marília, ${dataExtenso(format(new Date(), 'dd/MM/yyyy'))}`
  );
};

const COLUNAS_TIPO_ATESTADO = [
  [
    {
      id: 'Admissional',
      label: 'Admissional',
      tipo: 'Admissional',
      template: 'Admissional',
    },
    {
      id: 'AtestadoDia',
      label: 'Atestado Dia',
      tipo: 'Atestado Dia',
      template: 'AtestadoDia',
    },
    {
      id: 'AtestadoHora',
      label: 'Atestado Hora',
      tipo: 'Atestado Hora',
      template: 'AtestadoHora',
    },
    {
      id: 'AtestadoFutebol',
      label: 'Atestado Futebol',
      tipo: 'Atestado Futebol',
      template: 'AtestadoFutebol',
    },
    {
      id: 'AtividadeFisica',
      label: 'Atividade Fisica',
      tipo: 'Atividade Fisica',
      template: 'AtividadeFisica',
    },
    {
      id: 'CarteiraHAS',
      label: 'Carteira HAS',
      tipo: 'Carteira HAS',
      template: 'CarteiraHAS',
    },
  ],
  [
    { id: 'Cate', label: 'Cateterismo', tipo: 'Cateterismo', template: 'Cate' },
    {
      id: 'Cirurgia',
      label: 'Cirurgia',
      tipo: 'Cirurgia',
      template: 'Cirurgia',
    },
    {
      id: 'ConcursoPublico1',
      label: 'Concurso Público 1',
      tipo: 'Concurso Publico1',
      template: 'ConcursoPublico1',
    },
    {
      id: 'ConcursoPublico2',
      label: 'Concurso Público 2',
      tipo: 'Concurso Publico2',
      template: 'ConcursoPublico2',
    },
    {
      id: 'CriancaEsporte',
      label: 'Crianca Esporte',
      tipo: 'Crianca Esporte',
      template: 'CriancaEsporte',
    },
    {
      id: 'Declaracao',
      label: 'Declaracao',
      tipo: 'Declaracao',
      template: 'Declaracao',
    },
  ],
  [
    {
      id: 'AltoCusto',
      label: 'Alto Custo',
      tipo: 'Declaracao COVID',
      template: 'AltoCusto',
    },
    {
      id: 'Fisioterapia',
      label: 'Fisioterapia',
      tipo: 'Fisioterapia',
      template: 'Fisioterapia',
    },
    {
      id: 'Hidroginastica',
      label: 'Hidroginastica',
      tipo: 'Hidroginastica',
      template: 'Hidroginastica',
    },
    {
      id: 'Internacao',
      label: 'Internacao',
      tipo: 'Internacao',
      template: 'Internacao',
    },
    {
      id: 'Marcapasso',
      label: 'Marcapasso',
      tipo: 'Marcapasso',
      template: 'Marcapasso',
    },
    {
      id: 'Nutricionista',
      label: 'Nutricionista',
      tipo: 'Nutricionista',
      template: 'Nutricionista',
    },
  ],
  [
    {
      id: 'Psicologa',
      label: 'Psicóloga',
      tipo: 'Psicóloga',
      template: 'Psicologa',
    },
    {
      id: 'PeriopAltoRisco',
      label: 'Periop Alto Risco',
      tipo: 'Pre Operatório Alto Risco',
      template: 'PeriopAltoRisco',
    },
    {
      id: 'PeriopBaixoRisco',
      label: 'Periop Baixo Risco',
      tipo: 'Pre Operatório Baixo Risco',
      template: 'PeriopBaixoRisco',
    },
    { id: 'Vacina', label: 'Vacina', tipo: 'Vacina', template: 'Vacina' },
    {
      id: 'EstresseFisiologico',
      label: 'Estresse Fisiológico',
      tipo: 'Estresse Fisiológico',
      template: 'EstresseFisiologico',
      semData: true,
    },
    {
      id: 'Angiotomografia',
      label: 'Angiotomografia (TC)',
      tipo: 'Angiotomografia',
      template: 'Angiotomografia',
    },
  ],
];

function AtestadoMedico({ show, atual, medico, funcionario, onHide }) {
  const [isSavingAtestado, setIsSavingAtestado] = useState(false);
  const [showRadioAtestado, setShowRadioAtestado] = useState(false);
  const [assinaturaDigital, setAssinaturaDigital] = useState(false);
  const [signing, setSigning] = useState(false);
  const [atestadoMedico, setAtestadoMedico] = useState(() =>
    atestadoEmBranco(atual)
  );
  const codPac = show && atual?.CODPAC ? atual.CODPAC : null;
  const { data: atestados, isLoading, mutate: mutateAtestados } = useSWR(
    codPac ? ['atestados', codPac] : null,
    fetchAtestadosDoPaciente,
    { revalidateOnFocus: false, shouldRetryOnError: false }
  );

  const codFunc = medico?.idFunc || funcionario?.codFunc;
  const isDoctorLoggedIn = !!medico;
  const isFuncionario = !!funcionario;
  const imagensAnexas = obterImagensPorTipoAtestado(atestadoMedico.tipo);

  useEffect(() => {
    if (!atestados) return;
    if (!atestados.length) {
      setAtestadoMedico(atestadoEmBranco(atual));
      return;
    }
    setAtestadoMedico(toAtestadoSelecionado(atestados[0]));
  }, [atestados]);

  const handleVidaasAssinarAtestado = async content => {
    try {
      const vidaasStatus = await getVidaasStatus();
      if (!vidaasStatus?.authenticated) {
        toast.warn('Usuário ainda não autenticado no VIDAAS!');
        return null;
      }

      const base64Strip = removeBeforeBase64(content);
      const payload = {
        documentType: 'atestado',
        documentId: atestadoMedico.codAtestado.toString(),
        alias: atestadoMedico.codAtestado + '-atestado.pdf',
        signatureFormat: 'CAdES_AD_RT',
        hashAlgorithm: '2.16.840.1.101.3.4.2.1',
        base64Content: base64Strip,
      };

      setSigning(true);
      const resp = await signDocument(payload);

      if (resp?.fileBase64Signed) {
        return resp.fileBase64Signed;
      }

      toast.warn('Assinatura não encontrada!');
      return null;
    } catch (error) {
      toast.warn(
        error?.response?.data?.error || 'Usuário ainda não autenticado!'
      );
      return null;
    } finally {
      setSigning(false);
    }
  };

  const handleGravarAtestado = async () => {
    if (!atestadoMedico.codAtestado) {
      return toast.warn('Selecione um atestado!');
    }

    try {
      setIsSavingAtestado(true);
      const res = await updateAtestado(atestadoMedico);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao gravar atestado!');
    } finally {
      setIsSavingAtestado(false);
    }
  };

  const handleRepetirAtestado = async () => {
    const dataPorExtenso = format(new Date(), "dd 'de' MMMM 'de' yyyy", {
      locale: pt,
    });
    const linhas = (atestadoMedico?.descricao ?? '').split('\n');
    let indiceData = -1;

    for (let i = 0; i < linhas.length; i++) {
      if (linhas[i].includes('Marília')) indiceData = i;
    }

    if (indiceData >= 0) {
      linhas[indiceData] = `Marília, ${dataPorExtenso}.`;
    }

    const payload = {
      codConsulta: atestadoMedico.codConsulta,
      codMedico: medico ? medico?.codMed : atestadoMedico.codMedico,
      codPaciente: atestadoMedico.codPaciente,
      tipo: atestadoMedico.tipo,
      descricao: linhas.join('\n'),
    };

    try {
      const result = await insertAtestado(payload);
      if (result == null) return;
      mutateAtestados();
      setShowRadioAtestado(true);
      toast.success('Gerado novo atestado.');
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao inserir atestado!');
    }
  };

  const handlePrescreverNovoAtestado = async () => {
    if (!medico?.codMed) {
      return toast.warn(
        'Erro de validação: atestado só pode ser gerado para um médico!'
      );
    }

    try {
      const payload = {
        codConsulta: atestadoMedico.codConsulta,
        codMedico: medico.codMed,
        codPaciente: atestadoMedico.codPaciente,
        tipo: '',
        descricao: '',
      };
      const result = await insertAtestado(payload);
      if (result == null) return;
      mutateAtestados();
      setShowRadioAtestado(true);
      toast.success('Gerado novo atestado. Selecione o tipo!');
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao inserir atestado!');
    }
  };

  const imprimirOuEnviarAtestado = async (atestado, tipo) => {
    if (!atestado || !atestado.descricao) {
      toast.warn('Atestado sem descrição!');
      return;
    }

    const pdfGenerator = await generateAtestadoPDF({
      atestado,
      tipo,
      assinaturaDigital,
      codFunc,
      isDoctorLoggedIn,
    });

    if (tipo === 'imprimirNormal' || tipo === 'imprimirTimbrado') {
      if (assinaturaDigital) {
        const fileAtestado = await new Promise((resolve, reject) => {
          if (typeof pdfGenerator.getBase64 === 'function') {
            pdfGenerator.getBase64(base64 => {
              if (!base64) {
                reject(new Error('Falha ao gerar base64 do atestado'));
                return;
              }
              resolve(`data:application/pdf;base64,${base64}`);
            });
            return;
          }

          if (typeof pdfGenerator.getBlob === 'function') {
            pdfGenerator.getBlob(blob => {
              const reader = new FileReader();
              reader.onloadend = () => {
                if (typeof reader.result !== 'string') {
                  reject(new Error('Formato inesperado ao converter PDF'));
                  return;
                }
                resolve(reader.result);
              };
              reader.onerror = () =>
                reject(new Error('Falha ao converter blob para base64'));
              reader.readAsDataURL(blob);
            });
            return;
          }

          reject(new Error('Gerador de PDF sem suporte a base64'));
        });

        const signedBase64 = await handleVidaasAssinarAtestado(fileAtestado);
        if (signedBase64) openBase64Pdf(signedBase64);
        return;
      }

      pdfGenerator.open();
    } else if (tipo === 'envioWpp') {
      const celular = normalizeWhatsappPhone(atual && atual.CELULARPAC);
      if (!celular) {
        toast.warn('Número de celular não encontrado!');
        return;
      }

      try {
        let pdfBase64;

        if (assinaturaDigital) {
          const unsignedBase64 = await getPdfBase64(pdfGenerator);
          const signedBase64 = await handleVidaasAssinarAtestado(
            `data:application/pdf;base64,${unsignedBase64}`
          );
          if (!signedBase64) return;
          pdfBase64 = signedBase64;
        } else {
          pdfBase64 = await getPdfBase64(pdfGenerator);
        }

        const data = await enviarArquivo({
          pdfBase64,
          to: celular,
          paciente: {
            id: atestado?.codPaciente,
            nome: atestado?.nomePaciente,
            medico: atestado?.nomeMedico,
          },
          codLaudo: atestado?.codAtestado,
          fileName: `Atestado_${atestado?.codAtestado}.pdf`,
          caption: `Olá *${atestado?.nomePaciente}*, tudo bem? 😊
Segue em anexo o seu *Atestado Médico*.

Qualquer dúvida, nossa equipe estará à disposição no setor de atendimento pelo WhatsApp 14-3402-5252\n
ICM - Instituto do Coração de Marília`,
          officialConfig: {
            procedimento: 'atestado',
            documentTitle: 'Atestado Médico',
          },
        });

        toast.success(data.message);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error ||
          error?.message ||
          'Erro ao enviar atestado via WhatsApp';
        toast.error(errorMessage);
      }
    }
  };

  const handleTipoAtestado = tipoId => {
    const modelo = COLUNAS_TIPO_ATESTADO.flat().find(
      item => item.id === tipoId
    );
    if (!modelo) return;

    const nomePacienteSubstituir =
      (atual && atual.NOMEPAC) || atestadoMedico.nomePaciente || '';
    if (!nomePacienteSubstituir) {
      toast.warn(
        'Dados do paciente indisponíveis. Recarregue o prontuário e tente novamente.'
      );
      return;
    }

    const descricao = montarDescricaoAtestado(
      modelo.template,
      nomePacienteSubstituir,
      modelo.semData
    );
    setAtestadoMedico({
      ...atestadoMedico,
      tipo: modelo.tipo,
      descricao,
    });
  };

  return (
    <Modal show={show} size="xl" centered>
      <Modal.Header className="font-weight-bold">ATESTADO MÉDICO</Modal.Header>
      <Modal.Body style={{ display: 'flex' }}>
        {isLoading ? (
          <div
            className="d-flex justify-content-center align-items-center w-100 py-5"
            style={{ minHeight: 200 }}
          >
            <Spinner
              animation="border"
              role="status"
              aria-label="Carregando atestados"
            />
          </div>
        ) : (
          <>
            <div style={{ flex: '0 0 10.5%' }}>
              {atestados?.length > 0 ? (
                <ButtonGroup vertical>
                  {atestados.map(atestado => (
                    <Button
                      variant="outline-primary"
                      key={atestado.CODATESTADO}
                      onClick={() =>
                        setAtestadoMedico(toAtestadoSelecionado(atestado))
                      }
                    >
                      {atestado.dataFormatada}
                    </Button>
                  ))}
                </ButtonGroup>
              ) : (
                <i className="text-danger">Paciente não tem atestado!</i>
              )}
            </div>
            <Card style={{ flex: 1, display: 'flex', padding: '0.5rem' }}>
              <Form noValidate>
                <Form.Row>
                  <Form.Group as={Col} className="font-weight-bold">
                    Paciente: {atestadoMedico.nomePaciente}
                  </Form.Group>
                  <Form.Group as={Col} className="font-weight-bold">
                    Médico: {atestadoMedico.nomeMedico}
                  </Form.Group>
                </Form.Row>
                <Button onClick={handlePrescreverNovoAtestado}>
                  Novo Atestado
                </Button>{' '}
                <Button variant="secondary" onClick={handleRepetirAtestado}>
                  Repetir Atestado
                </Button>{' '}
                <hr></hr>
                {showRadioAtestado && (
                  <Card style={{ padding: '0.5rem' }}>
                    <strong>Tipos de Atestado</strong>
                    <div key="radio" style={{ fontSize: 12 }}>
                      <Row xs={12} sm={4} md={4} lg={4}>
                        {COLUNAS_TIPO_ATESTADO.map(coluna => (
                          <Col sm={3} key={coluna[0].id}>
                            {coluna.map(item => (
                              <div
                                key={item.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  marginBottom: 2,
                                }}
                              >
                                <Form.Check
                                  label={item.label}
                                  name="groupAtestado"
                                  inline
                                  type="radio"
                                  id={item.id}
                                  onClick={e => handleTipoAtestado(e.target.id)}
                                />
                              </div>
                            ))}
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </Card>
                )}
                <Form.Label className="font-weight-bold">
                  Descrição - {atestadoMedico.codAtestado} -{' '}
                  {atestadoMedico.dataAtestado}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  style={{ fontSize: '12px' }}
                  rows={15}
                  value={atestadoMedico.descricao ?? ''}
                  onChange={e =>
                    setAtestadoMedico({
                      ...atestadoMedico,
                      descricao: e.target.value,
                    })
                  }
                />
                {imagensAnexas.length > 0 && (
                  <Card className="mt-2 p-2">
                    <strong style={{ fontSize: 12 }}>
                      Imagens anexas ao documento (impressas na 2ª página do
                      PDF)
                    </strong>
                    <Row className="mt-2">
                      {imagensAnexas.map(img => (
                        <Col sm={6} key={img.id} className="text-center mb-2">
                          <div
                            className="text-muted mb-1"
                            style={{ fontSize: 11 }}
                          >
                            {img.title}
                          </div>
                          <img
                            src={img.src}
                            alt={img.title}
                            style={{
                              maxWidth: '100%',
                              maxHeight: 200,
                              objectFit: 'contain',
                              border: '1px solid #dee2e6',
                              borderRadius: 4,
                            }}
                          />
                        </Col>
                      ))}
                    </Row>
                  </Card>
                )}
              </Form>
              <Form.Check
                type="switch"
                id="atestado-switch"
                checked={assinaturaDigital}
                label="Assinatura Digital"
                onChange={e => setAssinaturaDigital(e.target.checked)}
              />
              <Card.Footer className="text-center">
                <Button
                  size="md"
                  onClick={handleGravarAtestado}
                  disabled={isSavingAtestado}
                >
                  {isSavingAtestado ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />{' '}
                      Gravando...
                    </>
                  ) : (
                    'Gravar'
                  )}
                </Button>{' '}
                <Button
                  variant="warning"
                  onClick={() =>
                    imprimirOuEnviarAtestado(atestadoMedico, 'imprimirNormal')
                  }
                >
                  Imprimir
                </Button>{' '}
                <Button
                  variant="info"
                  onClick={() =>
                    imprimirOuEnviarAtestado(atestadoMedico, 'imprimirTimbrado')
                  }
                >
                  Timbrado
                </Button>{' '}
                <Button
                  variant="outline-success"
                  disabled={isFuncionario}
                  onClick={() =>
                    imprimirOuEnviarAtestado(atestadoMedico, 'envioWpp')
                  }
                >
                  <FaWhatsapp /> Enviar PDF
                </Button>{' '}
                <Button
                  variant="danger"
                  onClick={() => {
                    setAssinaturaDigital(false);
                    onHide();
                  }}
                >
                  Fechar
                </Button>{' '}
                {signing && (
                  <Spinner animation="border" size="sm" role="status" />
                )}
              </Card.Footer>
            </Card>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default memo(AtestadoMedico);
